class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getCsiSearchUrl(searchTerm) {
    const config = window.CSI_SEARCH || {};
    const base = config.searchUrl || '/apps/csi-search/search';
    const params = new URLSearchParams({
      q: searchTerm,
      limit: '3',
      sortId: 'relevance',
    });
    return `${base}?${params.toString()}`;
  }

  getShopifySuggestUrl(searchTerm) {
    const base = window.routes?.predictive_search_url || '/search/suggest';
    const params = new URLSearchParams();
    params.set('q', searchTerm);
    // Products come from CSI; Shopify supplies collections / pages / articles / queries.
    params.set('resources[type]', 'query,collection,page,article');
    params.set('resources[limit]', '3');
    params.set('resources[limit_scope]', 'each');
    return `${base}.json?${params.toString()}`;
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  buildTextResultItem(id, url, title) {
    return `
      <li id="${id}" class="predictive-search__list-item" role="option" aria-selected="false">
        <a href="${this.escapeHtml(url)}" class="predictive-search__item link link--text" tabindex="-1">
          <div class="predictive-search__item-content predictive-search__item-content--centered">
            <p class="predictive-search__item-heading h5">${this.escapeHtml(title)}</p>
          </div>
        </a>
      </li>
    `;
  }

  buildCsiResultsMarkup(searchTerm, data) {
    const MAX_SUGGESTIONS = 3;
    const MAX_PAGES = 3;
    const MAX_PRODUCTS = 3;

    const config = window.CSI_SEARCH || {};
    const showVendor = config.showVendor !== false;
    const showPrice = config.showPrice !== false;
    const products = (Array.isArray(data?.products) ? data.products : []).slice(0, MAX_PRODUCTS);
    const collections = Array.isArray(data?.collections) ? data.collections : [];
    const pages = Array.isArray(data?.pages) ? data.pages : [];
    const articles = Array.isArray(data?.articles) ? data.articles : [];
    const queries = Array.isArray(data?.queries) ? data.queries : [];
    const total = data?.estimatedTotalHits ?? products.length;
    const searchUrl = `${window.routes?.search_url || '/search'}?scope=${encodeURIComponent(searchTerm)}`;

    // "Search for …" footer counts as 1 suggestion → max 2 query/collection hits above it.
    const normalizedTerm = searchTerm.trim().toLowerCase();
    const suggestionItems = [...queries, ...collections]
      .filter((item) => {
        const title = String(item.title || item.text || '').trim().toLowerCase();
        return title && title !== normalizedTerm;
      })
      .slice(0, Math.max(0, MAX_SUGGESTIONS - 1));

    const pageItems = [...pages, ...articles].slice(0, MAX_PAGES);
    const hasContent =
      products.length > 0 || pageItems.length > 0 || suggestionItems.length > 0;

    if (data?.ok === false && !hasContent) {
      return `
        <div id="predictive-search-results" role="listbox">
          <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions">
            <div class="predictive-search__result-group">
              <p class="predictive-search__item-heading h5" style="padding: 1rem;">Search unavailable. Please try again.</p>
            </div>
          </div>
          <span class="hidden" data-predictive-search-live-region-count-value>Search unavailable</span>
        </div>
      `;
    }

    const suggestionList = suggestionItems
      .map((item, index) => {
        const title = item.title || item.text || item.styled_text || '';
        const url = item.url || `${window.routes?.search_url || '/search'}?q=${encodeURIComponent(title)}`;
        return this.buildTextResultItem(`predictive-search-option-suggestion-${index + 1}`, url, title);
      })
      .join('');

    const pagesList = pageItems
      .map((item, index) => {
        const title = item.title || '';
        const url = item.url || '#';
        return this.buildTextResultItem(`predictive-search-option-page-${index + 1}`, url, title);
      })
      .join('');

    const productItems = products
      .map((product, index) => {
        const url = this.escapeHtml(product.url || `/products/${product.handle}`);
        const title = this.escapeHtml(product.title);
        const vendor = this.escapeHtml(product.vendor || '');
        const price = this.escapeHtml(product.price || '');
        const imageUrl = product.imageUrl || product.image?.url;
        const imageAlt = this.escapeHtml(product.imageAlt || product.image?.altText || product.title || '');
        const imageHtml = imageUrl
          ? `<img class="predictive-search__image" src="${this.escapeHtml(imageUrl)}" alt="${imageAlt}" width="50" height="50" loading="lazy">`
          : '';
        const vendorHtml =
          showVendor && vendor
            ? `<span class="visually-hidden">Vendor</span><div class="predictive-search__item-vendor caption-with-letter-spacing">${vendor}</div>`
            : '';
        const priceHtml =
          showPrice && price
            ? `<div class="price"><div class="price__container"><div class="price__regular"><span class="price-item price-item--regular">${price}</span></div></div></div>`
            : '';
        const contentClass =
          showVendor || showPrice
            ? 'predictive-search__item-content'
            : 'predictive-search__item-content predictive-search__item-content--centered';

        return `
          <li id="predictive-search-option-product-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
            <a href="${url}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
              ${imageHtml}
              <div class="${contentClass}">
                ${vendorHtml}
                <p class="predictive-search__item-heading h5">${title}</p>
                ${priceHtml}
              </div>
            </a>
          </li>
        `;
      })
      .join('');

    // Match storefront layout: suggestions (incl. current search) → Pages → Products → Search for
    const suggestionsBlock =
      suggestionItems.length > 0
        ? `
      <div class="predictive-search__result-group">
        <div>
          <h2 id="predictive-search-queries" class="predictive-search__heading text-body caption-with-letter-spacing visually-hidden">Suggestions</h2>
          <ul id="predictive-search-results-queries-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-queries">
            ${suggestionList}
          </ul>
        </div>
      </div>`
        : '';

    const pagesBlock =
      pageItems.length > 0
        ? `
      <div class="predictive-search__result-group">
        <div>
          <h2 id="predictive-search-pages" class="predictive-search__heading text-body caption-with-letter-spacing">Pages</h2>
          <ul id="predictive-search-results-pages-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-pages">
            ${pagesList}
          </ul>
        </div>
      </div>`
        : '';

    const productsBlock =
      products.length > 0
        ? `
      <div class="predictive-search__result-group">
        <div>
          <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">Products</h2>
          <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
            ${productItems}
          </ul>
        </div>
      </div>`
        : '';

    const emptyBlock = !hasContent
      ? `
      <div class="predictive-search__result-group">
        <p class="predictive-search__item-heading h5" style="padding: 1rem;">No results found for “${this.escapeHtml(searchTerm)}”</p>
      </div>`
      : '';

    const wrapperClass = [
      'predictive-search__results-groups-wrapper',
      suggestionItems.length === 0 ? 'predictive-search__results-groups-wrapper--no-suggestions' : '',
      products.length === 0 ? 'predictive-search__results-groups-wrapper--no-products' : '',
    ]
      .filter(Boolean)
      .join(' ');

    const liveParts = [];
    if (suggestionItems.length) liveParts.push(`${suggestionItems.length} suggestions`);
    if (pageItems.length) liveParts.push(`${pageItems.length} pages`);
    if (products.length) liveParts.push(`${products.length} products`);
    const liveText = !hasContent
      ? `No results found for ${searchTerm}`
      : `${total || products.length} results: ${liveParts.join(', ')}`;

    return `
      <div id="predictive-search-results" role="listbox">
        <div id="predictive-search-results-groups-wrapper" class="${wrapperClass}">
          ${suggestionsBlock}
          ${pagesBlock}
          ${productsBlock}
          ${emptyBlock}
        </div>
        <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
          <button
            class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow"
            tabindex="-1"
            role="option"
            aria-selected="false"
            type="submit"
            formaction="${this.escapeHtml(searchUrl)}"
          >
            <span data-predictive-search-search-for-text>Search for “${this.escapeHtml(searchTerm)}”</span>
            <span class="svg-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 10" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546z" clip-rule="evenodd"/></svg>
            </span>
          </button>
        </div>
        <span class="hidden" data-predictive-search-live-region-count-value>${this.escapeHtml(liveText)}</span>
      </div>
    `;
  }

  async fetchJson(url, signal) {
    const response = await fetch(url, {
      signal,
      headers: { Accept: 'application/json' },
    });
    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }
    return { ok: response.ok, data };
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    if (window.CsiSkeleton?.predictiveList) {
      this.predictiveSearchResults.innerHTML = window.CsiSkeleton.predictiveList(6);
      this.setAttribute('results', true);
      this.open();
    }

    this.abortController.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    Promise.all([
      this.fetchJson(this.getCsiSearchUrl(searchTerm), signal).catch(() => ({ ok: false, data: null })),
      this.fetchJson(this.getShopifySuggestUrl(searchTerm), signal).catch(() => ({ ok: false, data: null })),
    ])
      .then(([csiResult, shopifyResult]) => {
        const csiData = csiResult?.data || {};
        const shopifyResults = shopifyResult?.data?.resources?.results || {};

        const merged = {
          ok: csiData.ok !== false || Boolean(shopifyResult?.ok),
          products: Array.isArray(csiData.products) ? csiData.products : [],
          estimatedTotalHits: csiData.estimatedTotalHits,
          collections: Array.isArray(shopifyResults.collections) ? shopifyResults.collections : [],
          pages: Array.isArray(shopifyResults.pages) ? shopifyResults.pages : [],
          articles: Array.isArray(shopifyResults.articles) ? shopifyResults.articles : [],
          queries: Array.isArray(shopifyResults.queries) ? shopifyResults.queries : [],
        };

        // If CSI failed but Shopify returned content, still show content.
        if (csiData.ok === false && merged.collections.length + merged.pages.length + merged.articles.length === 0) {
          merged.ok = false;
        }

        const resultsMarkup = this.buildCsiResultsMarkup(searchTerm, merged);
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) {
          return;
        }
        const resultsMarkup = this.buildCsiResultsMarkup(searchTerm, { ok: false });
        this.renderSearchResults(resultsMarkup);
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    // Intentionally no visible "Loading" copy — skeletons handle pending state.
    if (this.statusElement) {
      this.statusElement.textContent = '';
      this.statusElement.setAttribute('aria-hidden', 'true');
    }
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegion = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegion?.textContent) {
      this.setLiveRegionText(liveRegion.textContent);
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
